import './AiAnalyst.scss';
import { useCallback, useEffect, useRef, useState } from 'react';
import SideBar from '../../components/SideBar/SideBar';
import TopNav from '../../components/TopNav/TopNav';
import api from '../../services/api';

// AI Analyst: ask ZONA's data a question in plain English.
//
// Sources:
//   - GET  /admin/ai-analyst/status  (is the model keyed, which datasets are live)
//   - POST /admin/ai-analyst/chat    { question, history } -> { answer, trace, ms }
//
// The model, the API key and every query live on the server. This page only
// renders a conversation; it holds no credentials and issues no queries of its
// own. Conversation state is client-side only, so a reload starts fresh --
// history is replayed to the server on each turn.
//
// The tool trace is shown per answer on purpose. The analyst runs on a free
// NVIDIA model against two datasets that disagree with each other, so every
// number it gives needs to be traceable to the query that produced it.

const SUGGESTIONS = [
  'What has [name] done today?',
  'How many users were active in the last 7 days and what were they doing?',
  'Which venues got the most attention this week?',
  'Where are people dropping out of signup?',
  'Has [name] ever requested a table?',
];

const DATASET_NOTE = 'ZONA tracking joins to venues, events and bookings. '
  + 'PostHog has signup, onboarding, push and pre-account activity. '
  + 'The analyst reads both and will tell you which one a number came from.';

const AiAnalyst = () => {
  const [status, setStatus] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [openTrace, setOpenTrace] = useState({});
  const scrollRef = useRef(null);

  useEffect(() => {
    api
      .get('/admin/ai-analyst/status')
      .then((res) => setStatus(res.data))
      .catch(() => setStatus({ ready: false }));
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, sending]);

  const send = useCallback(
    async (text) => {
      const question = (text ?? input).trim();
      if (!question || sending) return;

      setError(null);
      setInput('');
      // Snapshot the history BEFORE appending, so the server gets the
      // conversation as it stood when the question was asked.
      const history = messages.map((m) => ({ role: m.role, content: m.content }));
      setMessages((prev) => [...prev, { role: 'user', content: question }]);
      setSending(true);

      try {
        const res = await api.post('/admin/ai-analyst/chat', { question, history });
        setMessages((prev) => [
          ...prev,
          {
            role: 'assistant',
            content: res.data.answer,
            trace: res.data.trace || [],
            ms: res.data.ms,
            hitRoundLimit: res.data.hit_round_limit,
          },
        ]);
      } catch (err) {
        const message = err?.response?.data?.message || 'The analyst could not be reached.';
        setError(message);
        // Leave the question in the transcript but mark the failure inline, so
        // a failed turn can't silently look like it was never asked.
        setMessages((prev) => [...prev, { role: 'assistant', content: null, failed: message }]);
      } finally {
        setSending(false);
      }
    },
    [input, messages, sending]
  );

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <main>
      <TopNav />
      <div className="main-container">
        <SideBar />
        <article className="page-content">
          <section className="analyst">
            <div className="analyst__header">
              <div>
                <h1 className="analyst__title">AI Analyst</h1>
                <p className="analyst__subtitle">
                  Ask ZONA&apos;s data a question in plain English.
                </p>
              </div>
              {status && (
                <div className="analyst__status">
                  <span className={`analyst__dot ${status.ready ? 'is-on' : 'is-off'}`} />
                  {status.ready ? (
                    <span>
                      {status.model}
                      {status.datasets?.posthog === false && ' · PostHog not configured'}
                    </span>
                  ) : (
                    <span>Not configured on the server</span>
                  )}
                </div>
              )}
            </div>

            <div className="analyst__note">{DATASET_NOTE}</div>

            <div className="analyst__thread" ref={scrollRef}>
              {messages.length === 0 && (
                <div className="analyst__empty">
                  <p>Try one of these:</p>
                  <div className="analyst__suggestions">
                    {SUGGESTIONS.map((s) => (
                      <button
                        key={s}
                        type="button"
                        className="analyst__chip"
                        onClick={() => (s.includes('[name]') ? setInput(s) : send(s))}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {messages.map((m, i) => (
                <div key={i} className={`analyst__msg analyst__msg--${m.role}`}>
                  <div className="analyst__bubble">
                    {m.failed ? (
                      <span className="analyst__error-inline">{m.failed}</span>
                    ) : (
                      m.content
                    )}
                  </div>

                  {m.role === 'assistant' && !m.failed && (
                    <div className="analyst__meta">
                      {typeof m.ms === 'number' && <span>{(m.ms / 1000).toFixed(1)}s</span>}
                      {m.hitRoundLimit && (
                        <span className="analyst__warn">hit tool limit, answer may be partial</span>
                      )}
                      {!!m.trace?.length && (
                        <button
                          type="button"
                          className="analyst__trace-toggle"
                          onClick={() => setOpenTrace((p) => ({ ...p, [i]: !p[i] }))}>
                          {openTrace[i] ? 'Hide' : 'Show'} {m.trace.length} quer
                          {m.trace.length === 1 ? 'y' : 'ies'}
                        </button>
                      )}
                    </div>
                  )}

                  {m.role === 'assistant' && openTrace[i] && (
                    <div className="analyst__trace">
                      {m.trace.map((t, j) => (
                        <div key={j} className="analyst__trace-row">
                          <code>{t.tool}</code>
                          <span className="analyst__trace-args">{JSON.stringify(t.args)}</span>
                          <span className="analyst__trace-ms">{t.ms}ms</span>
                          {t.error && <span className="analyst__warn">{t.error}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}

              {sending && (
                <div className="analyst__msg analyst__msg--assistant">
                  <div className="analyst__bubble analyst__bubble--thinking">
                    <span />
                    <span />
                    <span />
                  </div>
                </div>
              )}
            </div>

            {error && <div className="analyst__error">{error}</div>}

            <div className="analyst__composer">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder="What has Jessica done today?"
                rows={2}
                disabled={sending || (status && !status.ready)}
              />
              <button
                type="button"
                onClick={() => send()}
                disabled={sending || !input.trim() || (status && !status.ready)}>
                {sending ? 'Thinking' : 'Ask'}
              </button>
            </div>
            <p className="analyst__disclaimer">
              Runs on a free model over live production data. It is instructed to say when
              something is not tracked rather than guess, but check the queries behind any
              number you plan to act on.
            </p>
          </section>
        </article>
      </div>
    </main>
  );
};

export default AiAnalyst;
